/**
 * Display names for the resources the current route names, read through the
 * queries the shell already runs.
 *
 * Both queries here are the ones other parts of the shell mount anyway — the
 * agent layout fetches `["agents", id]` and the sidebar fetches `["projects"]`
 * — so sharing their keys costs no extra round trip: React Query serves one
 * request to every observer. Nothing here probes for a name it was not already
 * going to have, and an unresolvable name is reported as missing so callers can
 * fall back to the route identifier instead of inventing one.
 */
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api";
import type { BreadcrumbContext } from "@/lib/navigation";
import { routeAgentId, routeProjectId } from "@/lib/navigation";
import { agentPath } from "@/lib/paths";
import type { AgentSummary, ProjectSummary } from "@/models";

export interface RouteResources {
  /** The agent id in the path, decoded, or nothing off an agent route. */
  readonly agentId: string | undefined;
  /** The agent record once it has arrived; the name is the display label. */
  readonly agent: AgentSummary | undefined;
  /**
   * The project the route's agent belongs to (#147), read from the agent
   * record. Nothing on a route that names no agent, or before the record
   * arrives: the breadcrumb then keeps its shorter fallback trail rather than
   * guessing a parent.
   */
  readonly projectId: string | undefined;
  /** Breadcrumb display names, each present only when actually resolved. */
  readonly names: BreadcrumbContext;
}

export function useRouteResources(pathname: string): RouteResources {
  const agentId = routeAgentId(pathname);
  const projectId = routeProjectId(pathname);
  const agent = useQuery({
    queryKey: ["agents", agentId ?? ""],
    queryFn: function fetchRouteAgent(): Promise<AgentSummary> {
      return apiFetch(agentPath(agentId ?? "", ""));
    },
    enabled: agentId !== undefined,
  });
  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: function fetchProjects(): Promise<readonly ProjectSummary[]> {
      return apiFetch("/projects");
    },
  });
  /**
   * The agent's own project (#147) when the agent record carries it, which is
   * what lets an agent trail pass through its project. The catalog query is
   * the one the shell already mounts, so resolving the name costs no request.
   */
  const agentProjectId = agent.data?.projectId;
  const resolvedProjectId = projectId ?? agentProjectId;
  const project =
    resolvedProjectId === undefined
      ? undefined
      : projects.data?.find(function named(candidate): boolean {
          return candidate.id === resolvedProjectId;
        });
  return {
    agentId,
    agent: agent.data,
    projectId: agentProjectId,
    names: {
      ...(agent.data === undefined ? {} : { agentName: agent.data.name }),
      ...(project === undefined ? {} : { projectName: project.name }),
      ...(agentProjectId === undefined ? {} : { projectId: agentProjectId }),
    },
  };
}
